import * as React from "react";
import { useNavigate } from "react-router";
import { useGetContractFamilyExpansionQuery } from "@/api/contract-families";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface Props {
  collectionId: string | undefined;
  /** The expanded family summary row; `null` keeps the dialog closed and the query skipped. */
  familyId: string | null;
  onClose: () => void;
}

export const FamilySourceDocumentsDialog: React.FC<Props> = ({ collectionId, familyId, onClose }) => {
  const navigate = useNavigate();
  const familyExpansionQuery = useGetContractFamilyExpansionQuery(
    {
      snapshotCollectionId: collectionId ?? "",
      familyId: familyId ?? "",
    },
    { skip: !collectionId || familyId === null },
  );

  return (
    <Dialog open={familyId !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Family source documents</DialogTitle>
          <DialogDescription>
            Verification-only source snapshots. These rows are not included in filters, exports, or bulk actions.
          </DialogDescription>
        </DialogHeader>
        {familyExpansionQuery.isLoading ? (
          <div className="py-6 text-sm text-gray-500" aria-live="polite">
            Loading family documents...
          </div>
        ) : familyExpansionQuery.isError ? (
          <div className="py-6 text-sm text-red-700">Unable to load visible family documents.</div>
        ) : (
          <ul className="max-h-[60vh] divide-y overflow-y-auto rounded-md border">
            {(familyExpansionQuery.data?.rows ?? []).map(row => (
              <li key={row.outcome_id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-950">{row.name}</div>
                  <div className="text-xs text-gray-500">{row.role}</div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/reports/outcome/${row.outcome_id}`)}
                >
                  Open snapshot
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
